import AlertIcon from "~icons/local/alert";
import Accordion from "@/lib/Accordion";
import { components } from "@/lib/ApiSchema";
import { ReactElement } from "react";
import { Tooltip } from "react-tooltip";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import $api from "@/lib/queries/openapi";
import { useParams } from "react-router";

function format(value: number) {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

export default function ExtractedInfo() {
  const { partId, teamSlug } = useParams();

  const { data, isLoading } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/requested_parts/{id}/extracted_info",
    {
      params: {
        path: {
          team_slug: teamSlug!,
          id: partId!,
        },
      },
    },
    {
      enabled: !!teamSlug && !!partId,
      refetchOnWindowFocus: false,
    },
  );

  return (
    <Accordion
      title="Part Details"
      className="bg-slate-50 rounded-sm px-2 text-sm"
      hideTitleOnExpand
      expanded
    >
      <div className="w-full flex divide-slate-200 divide-x text-sm">
        <Metadata data={data?.metadata ?? []} loading={isLoading} />
        {(data?.warnings ?? []).length > 0 && (
          <Warnings data={data?.warnings ?? []} loading={isLoading} />
        )}
      </div>
    </Accordion>
  );
}

function Metadata({
  data,
  loading,
}: {
  data: components["schemas"]["RequestedPartExtractedMetaDatum"][];
  loading: boolean;
}) {
  return (
    <Container title="General Part Info" className="w-full">
      {loading ? (
        <Skeleton count={3} />
      ) : (
        <div className="flex gap-x-4 flex-wrap">
          {data.map(({ name, value, unit }) => (
            <div key={name} className="flex gap-1">
              <div className="text-slate-500">{name}:</div>
              <div className="font-bold">
                {unit ? format(Number(value)) : value}
              </div>
              {unit && <div className="text-slate-500">{unit}</div>}
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

function Warnings({
  data,
  loading,
}: {
  data: components["schemas"]["RequestedPartExtractedWarning"][];
  loading: boolean;
}) {
  return (
    <Container title="Manufacturing Warnings" className="w-1/2">
      {loading ? (
        <Skeleton count={3} />
      ) : (
        <div className="flex flex-col gap-1">
          {data.map(({ name }) => (
            <div key={name} className="flex truncate gap-2 items-center">
              {name === "un-foldable-sheet-metal" && (
                <div data-tooltip-id={name} className="flex items-center gap-1">
                  <AlertIcon />
                  <span>Sheet metal part cannot be unfolded</span>
                </div>
              )}
              <Tooltip id={name} className="z-50">
                Sheet metal part cannot be unfolded, likely indicating it cannot
                <br />
                be produced via linear radius bending, likely due to formed
                <br />
                features
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

function Container({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactElement;
  className: string;
}) {
  return (
    <div className={`h-full px-2 flex flex-col gap-1 ${className}`}>
      <div className="font-medium">{title}</div>
      <div className="grow overflow-y-auto">{children}</div>
    </div>
  );
}
